import React, { useEffect, useState, lazy, Suspense, useMemo } from 'react'
import { Box, Fade, Typography } from '@mui/material';
import { NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

// STATE 👇
import { routeName } from '../../../routes/routeName';
import { PersonalRecordState } from './manageState';
import { setStep } from '../../../store/step-personal-management/step.slice';

// COMPONENTS 👇
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SchoolIcon from '@mui/icons-material/School';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import NoteOutlinedIcon from '@mui/icons-material/NoteOutlined';
import { ButtonBack, ButtonDraft, ButtonOutlined, ButtonPrimary } from '../../../components/button/ButtonCustoms';
import NavigateListCustom, { navigateDataProps } from '../../../components/navigation/NavigationListCustom';
import StepperCustom, { stepLabelType } from '../../../components/step/Stepper';
import { personalContextType } from './context/personal.context';
import { useTranslation } from 'react-i18next';
import PersonnelSkeleton from './skeleton/PersonnelSkeleton';
import ModalEditPassword from './modal/ModalEditPassword';
import StickyButton from '../../../components/button/StickyButton';
import DropdownButton from '../../../components/button/DropdownButton';
import { EmployeeApi } from '../../../api/employee/employee.api';
import { setLoading, unsetLoading } from '../../../store/loading/loading.slice';
import { decodeId, getToken } from '../../../utils/app.utils';
import UploadAvatar from '../../../components/upload/UploadAvatar';
// import InputCheckbox from '../../../components/input/inputCheckbox';


const PersonalRecord = lazy(() => import('./step/PersonalRecord'));
const AdditionalInformation = lazy(() => import('./step/AdditionalInformation'));
const Address = lazy(() => import('./step/Address'))
const EmploymentHistory = lazy(() => import('./step/EmploymentHistory'));
const TrainingHistory = lazy(() => import('./step/TrainingHistory'));
const EducationalBackground = lazy(() => import('./step/EducationalBackground'));
// const ArticlesAndResearch = lazy(() => import('./step/ArticlesAndResearch'));
const SummaryBiography = lazy(() => import('./step/SummaryBiography'));
const Research = lazy(() => import('./step/Research'));

type Props = {
    ONLY_VIEW: boolean;
    IS_PROFILE?: boolean;
}

const defaultState: personalContextType = {
    view: false,
    setView: () => { }
};
export const PersonalContextState = React.createContext<personalContextType>(defaultState);

const PersonnelManagementDetail: React.FC<Props> = ({ ONLY_VIEW, IS_PROFILE }: Props) => {
    const {
        // handleTestForm,
        formPersonalRecord,
        handleChangeFormPersonalRecord,
        errorMessage,
        handleSubmit,
        isValid,
        formParent,
        formAddress,
        formCurrentAddress,
        formRegistrationHouse,
        formTempleAddress,
        formWorkAddress,
        formStudentHistory,
        formWorkHistory,
        formTraining,
        formBiography,
        formArticle,
        formAdditionalInformation,
        handleAddEducation,
        setSameAddressAsIdCard,
        handleUpdateHistoryWork,
        handleUpdateTraining,
        handleAddTraining,
        handleAddWorkHistory,
        handleChangeFormPersonalRecordParent,
        handleDeleteEducationByIndex,
        handleDeleteWorkHistory,
        handleDeleteTraining,
        handleUpdateData,
        handleChangeField,
        handleChangeSocialMediaContact,
        handleChangeFieldInside,
        notEdit,
        handleChangeEnableEdit,
        handleDeleteAccount,
        handleGetDetail,
        handleDeleteHistory,
        handleUpdateHistory,
        handleUpdateEmployee,
        handleChangeCoverFile,
        coverFile,
        handleDownloadFile,
        handleUploadFileTraining,
        handleDeleteFileEducation,
        informationSiblings,
        handleChangeInformationSiblings,
        handleDeleteInformationSibling,
        handleAddInformationSibling,
        handleOptionLanguageAdditionalInformation,
        handleChangeAdditionalInformation,
        position_data,
        province_data,
        // handleTestForm
    } = PersonalRecordState({ IS_PROFILE })
    const { t } = useTranslation();
    const { id: paramId } = useParams();
    const id_decode = decodeId(paramId ?? "");
    const id = id_decode ? id_decode : paramId;
    const token = getToken();
    const location = useLocation();
    const navigate: NavigateFunction = useNavigate();
    const dispatch = useDispatch();
    const step: number = useSelector((state: any) => state?.step?.step);
    const is_accept_value: string[] = ['active', 'สิ้นสุดการเป็นบุคลากร']; // i18n-ignore
    const is_accept: boolean = IS_PROFILE ? true : is_accept_value.includes(formPersonalRecord?.status_employee)
    const [is_create_research, set_is_create_research] = useState<boolean>(false);

    // console.log(formPersonalRecord?.status_employee)

    const [onlyView, setOnlyView] = useState<boolean>(false);
    const [view, setView] = useState<boolean>(location.state?.view || defaultState.view);
    const [isValidFormPersonnel, setIsValidFormPersonnel] = useState<boolean>(false);
    const [isShowModalEditPassword, setIsShowModalEditPassword] = useState<boolean>(false);
    const allStep: stepLabelType[] = useMemo(() => [
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.PERSONAL_HISTORY'), isAccept: isValidFormPersonnel || ONLY_VIEW, icon: <AssignmentIndIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.ADDITIONAL_INFORMATION'), isAccept: false || ONLY_VIEW, icon: <FolderOpenOutlinedIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.ADDRESS'), isAccept: isValidFormPersonnel || ONLY_VIEW, icon: <ContactMailIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.EDUCATIONAL_HISTORY'), isAccept: formStudentHistory?.student_history_data.length > 0 || ONLY_VIEW, icon: <SchoolIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.WORK_HISTORY'), isAccept: formWorkHistory?.work_history_data?.length > 0 || ONLY_VIEW, icon: <BusinessCenterIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.TRAINING_HISTORY'), isAccept: formTraining?.training_data?.length > 0 || ONLY_VIEW, icon: <WorkspacePremiumIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.BRIEF_BIOGRAPHY'), isAccept: formTraining?.training_data?.length > 0 || ONLY_VIEW, icon: <ArticleIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.ARTICLES'), isAccept: !!id, icon: <NoteOutlinedIcon />, disabled: !notEdit && !ONLY_VIEW },
        { name: t('PERSONAL_MANAGEMENT.DETAIL.STEP.RESEARCH'), isAccept: false, icon: <NoteOutlinedIcon />, disabled: !notEdit && !ONLY_VIEW }
    ], [t, isValidFormPersonnel, formStudentHistory, formWorkHistory, formTraining, notEdit]);

    const navigateData: navigateDataProps[] = [
        {
            name: t("PERSONAL_MANAGEMENT.DETAIL.STEP.MANAGE_PERSONNEL"),
            key: "1",
            pathName: routeName.cms.PersonnelManagement,
        },
        ...(ONLY_VIEW ? [{
            key: "",
            name: t("PERSONAL_MANAGEMENT.DETAIL.STEP.PERSONNEL_INFORMATION"),
            pathName: routeName.cms.PersonnelManagement + `-detail/view/${id ? +id : 0}`
        }] :
            [
                !id
                    ? {
                        name: t("PERSONAL_MANAGEMENT.DETAIL.STEP.ADD_PERSONNEL"),
                        key: "2",
                        pathName: routeName.cms.PersonnelManagementDetail,
                    }
                    : {
                        name: t("PERSONAL_MANAGEMENT.DETAIL.STEP.PERSONNEL_INFORMATION"),
                        key: "2",
                        pathName: routeName.cms.PersonnelManagement + `-detail/${id ? +id : 0}`,
                    },
            ]),
    ];
    const handleDownloadPdf = async (id: number, token: string, view?: boolean) => {
        dispatch(setLoading());
        await EmployeeApi.export_pdf(id, token).then(res => {
            if (res.status < 300) {
                const response = res.data;
                const blob = new Blob([response], { type: 'application/pdf' });
                if (view) {
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                } else {
                    saveAs(blob, t("ส่งออกเอกสาร-บุคคล/pdf/{{first_name}} {{last_name}}.", { first_name: formPersonalRecord?.first_name, last_name: formPersonalRecord?.last_name }));
                }
            }
        }).catch((error) => {
            console.log(error)
        }).finally(() => {
            dispatch(unsetLoading());
        })
    }

    const handleBack = () => {
        if (step === 0) {
            navigate(-1)
        } else {
            dispatch(setStep(step - 1))
        }
        return;
    }
    const handleNext = () => {
        handleSubmit(true);
    }
    const handleNextView = () => {
        if (step === 0) {
            dispatch(setStep(step + 1))
        } else if (step === 7) {
            dispatch(setStep(0));
        } else {
            dispatch(setStep(step + 1));
        }
    }
    const handlePreviousView = () => {
        if (step === 0) {
            navigate(-1)
        } else {
            dispatch(setStep(step - 1));
        }
    }

    useEffect(() => {
        setIsValidFormPersonnel(isValid('check') ? false : true);
    }, [step, isValid])

    useEffect(() => {
        if (ONLY_VIEW) {
            setOnlyView(true);
        }
        if (!view && !IS_PROFILE) {
            handleChangeEnableEdit(false);
        }
        if (IS_PROFILE) {
            handleChangeEnableEdit(true);
        }
    }, [view])


    return (
        <PersonalContextState.Provider value={{ view, setView }}>
            <div className='h-auto'>
                <StickyButton enableButtonBottom />
                {/* <div className='border-2 bg-slate-500 text-white p-3'>
                    <InputCheckbox
                        style={{ color: "white" }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTestForm(e.target.checked)}
                        label='ทดสอบป้อนข้อมูล'
                    />
                </div> */}
                {!IS_PROFILE && (
                    <>
                        <section className='flex justify-between flex-wrap gap-y-4'>
                            <article>
                                <NavigateListCustom
                                    navigateData={navigateData}
                                />
                            </article>
                            <article>
                                {is_accept && (
                                    <DropdownButton
                                        fullWidth
                                        sx={{ whiteSpace: "nowrap" }}
                                        // sx={{ fontSize: 'clamp(.3vw + 0.2vmin)'}}
                                        buttonName={t("PERSONAL_MANAGEMENT.BUTTON.DOWNLOAD_FILE")}
                                        menuItems={[
                                            {
                                                id: 1, name: t("PERSONAL_MANAGEMENT.BUTTON.DOWNLOAD_FILE"), onClick: () => {
                                                    if (id && token) {
                                                        handleDownloadPdf(+id, token);
                                                    }
                                                }
                                            }, {
                                                id: 2, name: t("PERSONAL_MANAGEMENT.BUTTON.VIEW_EXAMPLE"), onClick: () => {
                                                    if (id && token) {
                                                        handleDownloadPdf(+id, token, true);
                                                    }
                                                }
                                            }
                                        ]}
                                    />
                                )}
                            </article>
                        </section>
                        <section className='my-4'>
                            <Typography variant='h3'>
                                {!view ? t("PERSONAL_MANAGEMENT.DETAIL.STEP.ADD_PERSONNEL") : t("PERSONAL_MANAGEMENT.DETAIL.STEP.PERSONNEL_INFORMATION")}
                            </Typography>
                        </section>
                        <section className='w-full'>
                            <StepperCustom
                                sx={{ maxWidth: "fit-content" }}
                                step={step}
                                setStep={(num: number) => {
                                    dispatch(setStep(num));
                                }}
                                stepLabel={allStep}
                                view={is_accept || IS_PROFILE}
                            />
                        </section>
                    </>
                )}



                <Fade in={true} unmountOnExit className='w-full h-full'>
                    <div className='w-full h-auto'>
                        {IS_PROFILE && (
                            <>
                                <section className='flex flex-wrap gap-4 justify-start md:justify-start w-full items-center'>
                                    <span className='w-full max-w-fit'>
                                        <UploadAvatar
                                            filePath={formPersonalRecord?.employee_image?.length > 0 ? formPersonalRecord?.employee_image[0]?.file_path : ""}
                                            onDrop={(data) => handleChangeFormPersonalRecord('profile', data[0])}
                                            file={formPersonalRecord.profile}
                                            helperText={t(errorMessage?.profile)}
                                            // noClick={disabled}
                                            // hiddenDeleteButton={disabled}
                                            onDelete={() => {
                                                // if (profileImage) {
                                                //     handleChangeFormPersonalRecord('profile', null);
                                                //     handleChangeFormPersonalRecord('is_profile', false);
                                                //     setProfileImage(null);
                                                // } else {
                                                //     handleChangeFormPersonalRecord('profile', null)
                                                // }
                                            }}
                                        />
                                    </span>
                                    <span className='flex-1 flex justify-start items-end'>
                                        <StepperCustom
                                            sx={{ maxWidth: "fit-content" }}
                                            step={step}
                                            setStep={(num: number) => {
                                                dispatch(setStep(num));
                                            }}
                                            stepLabel={allStep}
                                            view={true}
                                        />
                                    </span>
                                </section>
                            </>
                        )}
                        {step === 0 && (
                            <Suspense fallback={<PersonnelSkeleton num={5} />}>
                                <PersonalRecord
                                    formPersonalRecord={formPersonalRecord}
                                    formParent={formParent}
                                    handleChangeFormPersonalRecordParent={handleChangeFormPersonalRecordParent}
                                    handleChangeFormPersonalRecord={handleChangeFormPersonalRecord}
                                    errorMessage={errorMessage}
                                    disabled={(notEdit && is_accept) || onlyView}
                                    informationSiblings={informationSiblings}
                                    handleChangeInformationSiblings={handleChangeInformationSiblings}
                                    handleDeleteInformationSibling={handleDeleteInformationSibling}
                                    handleAddInformationSibling={handleAddInformationSibling}
                                    position_data={position_data}
                                    province_data={province_data}
                                    tab_custom={IS_PROFILE ? <></> : undefined}
                                />
                            </Suspense>
                        )}
                        {step === 1 && (
                            <Suspense fallback={<PersonnelSkeleton size={200} num={3} hiddenProfile />}>
                                <AdditionalInformation
                                    disabled={(notEdit && is_accept) || onlyView}
                                    handleChangeAdditionalInformation={handleChangeAdditionalInformation}
                                    handleOptionLanguageAdditionalInformation={handleOptionLanguageAdditionalInformation}
                                    formaAdditionalInformation={formAdditionalInformation}
                                />
                            </Suspense>
                        )}
                        {step === 2 && (
                            <Suspense fallback={<PersonnelSkeleton size={200} hiddenProfile num={3} />}>
                                <Address
                                    formAddress={formAddress}
                                    formRegistrationHouse={formRegistrationHouse}
                                    formCurrentAddress={formCurrentAddress}
                                    formTempleAddress={formTempleAddress}
                                    formWorkAddress={formWorkAddress}
                                    setSameAddressAsIdCard={setSameAddressAsIdCard}
                                    handleChangeField={handleChangeField}
                                    errorMessage={errorMessage}
                                    disabled={(notEdit && is_accept) || onlyView}
                                    province_data={province_data}
                                />
                            </Suspense>
                        )}
                        {step === 3 && (
                            <Suspense fallback={<PersonnelSkeleton num={1} hiddenProfile isPublish={1} />}>
                                <EducationalBackground
                                    handleUpdateEmployee={handleUpdateEmployee}
                                    handleUpdateHistory={handleUpdateHistory}
                                    handleDeleteHistory={handleDeleteHistory}
                                    handleGetDetail={handleGetDetail}
                                    handleDeleteEducationByIndex={handleDeleteEducationByIndex}
                                    handleUpdateData={handleUpdateData}
                                    handleAddEducation={handleAddEducation}
                                    formStudentHistory={formStudentHistory}
                                    handleChangeField={handleChangeField}
                                    disabled={false || onlyView}
                                    handleSubmit={handleSubmit}
                                />
                            </Suspense>

                        )}

                        {step === 4 && (
                            <Suspense fallback={<PersonnelSkeleton num={1} hiddenProfile isPublish={1} />}>
                                <EmploymentHistory
                                    handleDeleteHistory={handleDeleteHistory}
                                    handleGetDetail={handleGetDetail}
                                    handleUpdateEmployee={handleUpdateEmployee}
                                    handleAddWorkHistory={handleAddWorkHistory}
                                    formPersonalRecord={formPersonalRecord}
                                    handleUpdateHistoryWork={handleUpdateHistoryWork}
                                    handleDeleteWorkHistoryByIndex={handleDeleteWorkHistory}
                                    handleUpdateData={handleUpdateData}
                                    formWorkHistory={formWorkHistory}
                                    disabled={notEdit && is_accept}
                                    position_data={position_data}
                                />
                            </Suspense>
                        )}
                        {step === 5 && (
                            <Suspense fallback={<PersonnelSkeleton num={1} hiddenProfile isPublish={1} />}>
                                <TrainingHistory
                                    handleDownloadFile={handleDownloadFile}
                                    handleDeleteHistory={handleDeleteHistory}
                                    handleUpdateHistory={handleUpdateHistory}
                                    handleGetDetail={handleGetDetail}
                                    handleUpdateEmployee={handleUpdateEmployee}
                                    handleAddTraining={handleAddTraining}
                                    formPersonalRecord={formPersonalRecord}
                                    handleUpdateTraining={handleUpdateTraining}
                                    handleDeleteTraining={handleDeleteTraining}
                                    formTraining={formTraining}
                                    handleUploadFileTraining={handleUploadFileTraining}
                                    handleDeleteFileTraining={handleDeleteFileEducation}
                                />
                            </Suspense>
                        )}
                        {step === 6 && (
                            <Suspense fallback={<PersonnelSkeleton num={2} hiddenProfile isPublish={1} />}>
                                <SummaryBiography
                                    coverFile={coverFile}
                                    handleChangeCoverFile={handleChangeCoverFile}
                                    formBiography={formBiography}
                                    handleChangeField={handleChangeField}
                                    handleChangeFieldInside={handleChangeFieldInside}
                                    handleChangeSocialMediaContact={handleChangeSocialMediaContact}
                                    disabled={notEdit && is_accept}
                                    id_employee={id ? +id : 0}
                                />
                            </Suspense>
                        )}
                        {step === 7 && id && (
                            <Suspense fallback={<PersonnelSkeleton num={2} hiddenProfile isPublish={1} />}>
                                {/* <ArticlesAndResearch
                                    formArticle={formArticle}
                                    handleChangeField={handleChangeField}
                                    disabled={notEdit && is_accept}
                                /> */}
                                <Research
                                    accept_article
                                    id_employee={+id}
                                    set_is_create_research={set_is_create_research}
                                    is_create_research={is_create_research}
                                />
                            </Suspense>
                        )}
                        {step === 8 && id && (
                            <Suspense fallback={<PersonnelSkeleton num={2} hiddenProfile isPublish={1} />}>
                                <Research
                                    id_employee={+id}
                                    set_is_create_research={set_is_create_research}
                                    is_create_research={is_create_research}
                                />
                            </Suspense>
                        )}


                        {!is_create_research && (
                            <>
                                {onlyView ?
                                    // VIEW ONLY
                                    <Box className="m-auto w-full flex flex-wrap justify-center items-center my-4 gap-4">
                                        <ButtonBack
                                            onClick={handlePreviousView}
                                            buttonName={t("PERSONAL_MANAGEMENT.BUTTON.BACK")}
                                        />
                                        <ButtonPrimary
                                            onClick={handleNextView}
                                            mode='next'
                                            buttonName={t("BUTTON.NEXT")}
                                        />
                                    </Box>
                                    :
                                    <React.Fragment>
                                        {((notEdit || !is_accept)) ? (
                                            <section className='w-full my-8 flex flex-wrap justify-center items-center gap-4'>
                                                <ButtonBack
                                                    onClick={handleBack}
                                                    buttonName={t("PERSONAL_MANAGEMENT.BUTTON.BACK")}
                                                />
                                                {is_accept ?
                                                    (
                                                        <>
                                                            {!(step === 3 || step === 5) && (
                                                                <ButtonPrimary
                                                                    onClick={() => {
                                                                        handleChangeEnableEdit(false);
                                                                    }}
                                                                    buttonName={t("PERSONAL_MANAGEMENT.BUTTON.EDIT")}
                                                                />
                                                            )}
                                                            <ButtonPrimary
                                                                mode='password'
                                                                onClick={() => setIsShowModalEditPassword(true)}
                                                                buttonName={t("PERSONAL_MANAGEMENT.BUTTON.EDIT_PASSWORD")}
                                                            />
                                                        </>
                                                    )
                                                    :
                                                    <ButtonDraft
                                                        onClick={() => handleSubmit(false, true)}
                                                        buttonName={t("PERSONAL_MANAGEMENT.BUTTON.SAVE_DRAFT")}
                                                    />
                                                }
                                                {(id && !IS_PROFILE) && (
                                                    <ButtonPrimary
                                                        mode='del'
                                                        onClick={() => handleDeleteAccount()}
                                                        buttonName={t("PERSONAL_MANAGEMENT.BUTTON.DELETE_ACCOUNT")}
                                                    />
                                                )}
                                                {!is_accept && (
                                                    <>
                                                        {step === 8 ?
                                                            <ButtonPrimary
                                                                onClick={() => {
                                                                    if (allStep[step - 1]?.isAccept) {
                                                                        handleSubmit(true);
                                                                    }
                                                                    return false
                                                                }}
                                                                mode='next'
                                                                buttonName={t("BUTTON.CONFIRM")}
                                                            />
                                                            :
                                                            <ButtonPrimary
                                                                onClick={() => {
                                                                    if (allStep[step]?.isAccept || step === 0 || step === 1) {
                                                                        if (step === 0 && isValidFormPersonnel) {
                                                                            dispatch(setStep(1))
                                                                        } else if (step === 1 && isValidFormPersonnel) {
                                                                            dispatch(setStep(2))
                                                                        } else {
                                                                            handleNext();
                                                                        }
                                                                    }
                                                                    return false
                                                                }}
                                                                mode='next'
                                                                buttonName={t("BUTTON.NEXT")}
                                                            />
                                                        }
                                                    </>
                                                )}
                                            </section>
                                        ) :
                                            <section className='animate-bounce w-full my-8 flex flex-wrap justify-center items-center gap-4'>
                                                <ButtonOutlined
                                                    onClick={() => {
                                                        handleChangeEnableEdit(true);
                                                    }}
                                                    buttonName={t("BUTTON.CANCEL")}
                                                />
                                                <ButtonPrimary
                                                    mode='success'
                                                    onClick={() => {
                                                        handleSubmit(true, undefined, () => {
                                                            handleChangeEnableEdit(true);
                                                        });//รอเพิ่ม field เลขที่
                                                    }}
                                                    buttonName={t("BUTTON.SUBMIT")}
                                                />
                                            </section>
                                        }
                                    </React.Fragment>
                                }
                            </>
                        )}
                    </div>
                </Fade>
                <ModalEditPassword
                    onClose={(status) => setIsShowModalEditPassword(status)}
                    open={isShowModalEditPassword}
                    callback={() => id && handleGetDetail(+id)}
                />
            </div>
        </PersonalContextState.Provider>
    )
}

export default PersonnelManagementDetail